import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";

import { cn } from "@pontimed/ui";

import {
  COUNTRY_PHONES,
  countryFlag,
  findCountryPhone,
  type CountryPhone,
} from "@/config/phone-prefixes";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface PhoneInputProps {
  /** Phone number WITHOUT prefix */
  value: string;
  onChange: (value: string) => void;
  /** ISO 3166-1 alpha-2 country code (lowercase). Controls the selected prefix. */
  countryCode?: string;
  /** Called when user picks a different country from the dropdown */
  onCountryChange?: (code: string, prefix: string) => void;
  /** Initial country if uncontrolled (defaults to "ro") */
  defaultCountry?: string;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  enterKeyHint?: "search" | "enter" | "done" | "go" | "next" | "previous" | "send";
}

export function PhoneInput({
  value,
  onChange,
  countryCode: controlledCountry,
  onCountryChange,
  defaultCountry = "ro",
  placeholder,
  id,
  autoFocus,
  autoComplete = "tel",
  className,
  disabled,
  onKeyDown,
  enterKeyHint,
}: PhoneInputProps) {
  const { t } = useLingui();
  const [internalCountry, setInternalCountry] = useState(defaultCountry);
  const selectedCode = controlledCountry ?? internalCountry;
  const country = findCountryPhone(selectedCode);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      if (!isMobile) {
        const timer = setTimeout(() => searchRef.current?.focus(), 50);
        return () => clearTimeout(timer);
      }
    }
  }, [open]);

  const filtered = search.trim()
    ? COUNTRY_PHONES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.prefix.includes(search) ||
          c.code.includes(search.toLowerCase()),
      )
    : COUNTRY_PHONES;

  function selectCountry(c: CountryPhone) {
    setInternalCountry(c.code);
    onCountryChange?.(c.code, c.prefix);
    setOpen(false);
  }

  return (
    <div className={cn("flex gap-1.5", className)}>
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setSearch("");
        }}
      >
        <PopoverTrigger
          disabled={disabled}
          className={cn(
            "border-input hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-ring/50 flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded-lg border bg-transparent px-2 text-sm transition-colors focus-visible:ring-3 disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          <span className="text-base leading-none">{countryFlag(country.code)}</span>
          <span className="text-muted-foreground text-xs">{country.prefix}</span>
          <ChevronDown className="text-muted-foreground size-3" />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-0">
          <div className="border-border border-b p-2">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search…`}
              className="placeholder:text-muted-foreground/40 h-8 w-full rounded-md border-0 bg-transparent px-2 text-base outline-none md:text-sm"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="text-muted-foreground px-2 py-3 text-center text-xs">
                {t`No results`}
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => selectCountry(c)}
                className={cn(
                  "hover:bg-muted/80 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                  c.code === selectedCode && "bg-muted font-medium",
                )}
              >
                <span className="text-base leading-none">{countryFlag(c.code)}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-muted-foreground text-xs">{c.prefix}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <input
        id={id}
        type="tel"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        enterKeyHint={enterKeyHint}
        placeholder={placeholder ?? "712 345 678"}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        disabled={disabled}
        className={cn(
          "border-input placeholder:text-muted-foreground/40 focus-visible:border-ring focus-visible:ring-ring/50 disabled:bg-input/50 dark:bg-input/30 h-8 min-w-0 flex-1 rounded-lg border bg-transparent px-2.5 py-1 text-base transition-colors outline-none placeholder:opacity-40 focus-visible:ring-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        )}
      />
    </div>
  );
}

export { findCountryPhone, COUNTRY_PHONES, countryFlag } from "@/config/phone-prefixes";
